import { useState } from 'react';
import { FeedDto, FeedLinkDto } from '@/api/feed';
import { useRouter } from 'next/navigation';
import { PhotoPickerFile } from '@/components/common/photo-picker';
import api from '@/api';
import { useToast } from '@/context/toast-context';
import { dataUrlToFile } from '@/libs/utils/file';

interface UseFeedUploadProps {
  feedId?: string;
  onUploadStart?: () => void;
  onUploadComplete?: (feedId?: string) => void;
  onUploadError?: (error: any) => void;
}

interface UseFeedUploadReturn {
  isLoading: boolean;
  uploadProgress: number;
  currentFileIndex: number;
  totalFiles: number;
  isFeedRegistering: boolean;
  uploadFeed: (photos: PhotoPickerFile[], caption: string, links: FeedLinkDto[]) => Promise<void>;
}

/**
 * Custom hook to handle feeds upload process
 */
export const useFeedUpload = ({
  feedId,
  onUploadStart,
  onUploadComplete,
  onUploadError,
}: UseFeedUploadProps = {}): UseFeedUploadReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [totalFiles, setTotalFiles] = useState(0);
  const [isFeedRegistering, setIsFeedRegistering] = useState(false);
  const router = useRouter();
  const toast = useToast();

  /**
   * Upload files and create feeds
   */
  const uploadFeed = async (photos: PhotoPickerFile[], caption: string, links: FeedLinkDto[]) => {
    try {
      setIsLoading(true);
      setIsFeedRegistering(false);
      setUploadProgress(0);
      if (onUploadStart) onUploadStart();

      // Filter unuploaded files and upload them sequentially
      const unuploadedFiles = photos.filter(file => !file.isUploaded);
      const uploadedFiles: PhotoPickerFile[] = [];
      
      setTotalFiles(unuploadedFiles.length);
      setCurrentFileIndex(0);

      let index = 0;
      for (const file of unuploadedFiles) {
        index++;
        const currentFileIdx = index;
        setCurrentFileIndex(currentFileIdx);
        setUploadProgress(0);

        const handleProgress = (progress: number) => {
          // 현재 처리 중인 파일이 맞는지 확인 (비동기 병렬 처리 대비)
          setUploadProgress(Math.round(progress));
        };

        if (file.localFile) {
          const uploadedFile = await api.file.upload(file.localFile, handleProgress);
          uploadedFiles.push({ ...file, isUploaded: true, file: uploadedFile });
        } else if (file.base64) {
          const generated = await dataUrlToFile(
            file.base64,
            file.fileName || `image_${Date.now()}`,
            file.mimeType
          );
          const uploadedFile = await api.file.upload(generated, handleProgress);
          uploadedFiles.push({ ...file, isUploaded: true, file: uploadedFile });
        } else {
          uploadedFiles.push(file);
        }
      }

      setIsFeedRegistering(true);

      // Merge uploaded files with already uploaded files
      const mergedFiles = photos.map(file => {
        if (!file.isUploaded) {
          // Find the corresponding uploaded file
          const uploadedFile = uploadedFiles.find(
            uploadedFile => uploadedFile.sortOrder === file.sortOrder
          );
          return uploadedFile || file;
        }
        return file;
      });

      // Create the feeds DTO
      const feedDto: FeedDto = {
        caption,
        files: mergedFiles.map(f => ({
          id: f.id,
          file: f.file,
          links: f.links,
          sortOrder: f.sortOrder
        })),
        links: links,
        hashtags: []
      };

      let resultFeedId: string | undefined;

      if (feedId) {
        // Update existing feeds
        feedDto.sid = feedId;
        await api.feed.update(feedDto);
        resultFeedId = feedId;
        console.log('Feed updated successfully');
      } else {
        // Create new feeds
        const createdFeed = await api.feed.write(feedDto);
        resultFeedId = createdFeed?.sid;
        console.log('Feed created successfully');
      }

      // Navigate to feeds details page
      if (resultFeedId) {
        if (onUploadComplete) onUploadComplete(resultFeedId);
        router.replace(`/feeds/${resultFeedId}`);
      } else {
        console.log('Feed operation completed, but no ID was returned');
        if (onUploadComplete) onUploadComplete();
      }
    } catch (error: any) {
      console.error('Error submitting feeds:', error);
      toast.showToast(error.message);
      if (onUploadError) onUploadError(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    uploadProgress,
    currentFileIndex,
    totalFiles,
    isFeedRegistering,
    uploadFeed,
  };
};
